import { forwardRef, useMemo, useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { CLASS_ROUTE } from "@/constant/urlConstant";
import SwiperKlass from "component/klass/SwiperKlass";
import CourseCard from "component/klass/CourseCard";
import ProseMirrorEditorView from "component/prosemirror/ProseMirrorEditorView";
import Curriculum from "component/klass/Curriculum";
import { cls, scrollTopList, isMo } from "utils/util";
import { isOfflineClass } from "utils/marketUtil";
import EditorView from "shared/EditorView";
import { AllCourses } from "types/klass";
import RecommendedSeminarItem from "page/management/seminar/RecommendedSeminarItem";
import {
  useCourseRelatedContents,
  useLectureRelatedCourses,
  useCoursesSearch,
} from "@/api/klass/hooks";
import { useCourseRelatedSeminar } from "@/api/seminar/hooks";
import ZoomImg from "component/ZoomImg";
import LectureFooter from "@/component/klass/LectureFooter";

const KLASS_DETAIL_TAB = {
  intro: "강의소개",
  curriculum: "커리큘럼",
  recommend: "추천",
  faq: "FAQ 및 공지",
} as const;

const KLASS_DETAIL_TAB_KEYS = Object.keys(KLASS_DETAIL_TAB) as KlassDetailTab[];

const INIT_KLASS_DETAIL_TAB_KEY = KLASS_DETAIL_TAB_KEYS[0];

export type KlassDetailTab = keyof typeof KLASS_DETAIL_TAB;

type LectureContentProps = {
  courseId: string;
  lectureList: AllCourses;
  defaultCourseId?: string;
} & LectureContentActions;

interface LectureContentActions {
  scrollToCurriculum: () => void;
}

const LectureContent = forwardRef<HTMLDivElement, LectureContentProps>(
  ({ courseId, lectureList, defaultCourseId, scrollToCurriculum }, forwardedRef) => {
    const [tab, setTab] = useState<KlassDetailTab>(INIT_KLASS_DETAIL_TAB_KEY);
    const [moreIntro, setMoreIntro] = useState(false);
    const isMobile = isMo();

    const introRef = useRef<HTMLDivElement | null>(null);
    const recommendRef = useRef<HTMLDivElement | null>(null);
    const faqRef = useRef<HTMLDivElement | null>(null);

    const [scroll, setScroll] = useState(false);

    // MARK: react-query dependency에 사용함.
    const lecturers = useMemo(
      () => lectureList.course.lecturerNames?.join(","),
      [lectureList.course.lecturerNames],
    );

    // MARK: 강사 검색
    const { data: lecturerCourses = [] } = useCoursesSearch({
      lecturers,
    });

    // MARK: 강연자의 또 다른 강의 id 조회
    const { data: courseRelatedClassIds } = useCourseRelatedContents(
      {
        productId: lectureList.course.products?.[0]?.productId,
        contentType: "CLASS",
      },
      {
        enabled: !!lectureList.course.products?.[0]?.productId,
      },
    );
    // MARK: 강연자의 또 다른 강의
    const { data: lectureRelatedCourses = [] } = useLectureRelatedCourses(
      courseRelatedClassIds?.list || [],
    );

    // MARK: 강연자의 또 다른 강의 데이터(강연자의 또 다른 강의 + 운영진이 툴즈로 세팅한 강의)
    const lecturerOtherCourses = useMemo(() => {
      // MARK: 강연자의 다른 강의들(현재 강의 제외)
      const filteredLecturerCourses =
        lecturerCourses?.filter((course) => course?.courseId !== courseId) || [];

      // MARK: 운영진이 툴즈로 세팅한 강의들과 강연자의 강의가 중복되지 않게 필터링
      const lecturerCourseIds = new Set(filteredLecturerCourses.map((course) => course?.courseId));
      const filteredRelatedCourses =
        lectureRelatedCourses?.filter(
          (course) => course?.courseId && !lecturerCourseIds.has(course?.courseId),
        ) || [];

      // MARK: filteredLecturerCourses, filteredRelatedCourses 데이터 매핑
      const lecturerCoursesData = filteredLecturerCourses.map((course) => ({
        courseId: course?.courseId,
        thumbnailImage: course?.thumbnailImageUrl,
        lecturers: course?.lecturers,
        title: course?.title,
        groupedBadges: course?.groupedBadges,
        regularPrice: course?.product?.regularPrice,
        salePrice: course?.product?.salePrice,
        isFree: course?.isFree,
      }));

      const lecturerRelatedCoursesData = filteredRelatedCourses.map((course) => ({
        courseId: course?.courseId,
        thumbnailImage: course?.thumbnailImage?.link,
        lecturers: course?.lecturers?.map((lecturer) => lecturer.name) || [],
        title: course?.title,
        groupedBadges: course?.groupedBadges,
        regularPrice: course?.products?.[0]?.regularPrice,
        salePrice: course?.products?.[0]?.salePrice,
        isFree: course?.isFree,
      }));

      return [...lecturerCoursesData, ...lecturerRelatedCoursesData];
    }, [lectureRelatedCourses, lecturerCourses, courseId]);

    // MARK: 추천 세미나 id 조회
    const { data: courseRelatedSeminarIds } = useCourseRelatedContents(
      {
        productId: lectureList.course.products?.[0]?.productId,
        contentType: "SEMINAR",
      },
      {
        enabled: !!lectureList.course.products?.[0]?.productId,
      },
    );
    // MARK: 추천 세미나 상세 정보 조회
    const { data: courseRelatedSeminars = [] } = useCourseRelatedSeminar(
      courseRelatedSeminarIds?.list || [],
    );

    // MARK: 추천 탭 노출 여부
    const isShowRecommendTab = lecturerOtherCourses.length > 0 || courseRelatedSeminars.length > 0;

    const onTabClick = (tab: KlassDetailTab) => {
      setTab(tab);

      switch (tab) {
        case "intro":
          scrollTopList(introRef, "smooth");
          break;
        case "curriculum":
          scrollToCurriculum();
          break;
        case "recommend":
          scrollTopList(recommendRef, "smooth");
          break;
        case "faq":
          scrollTopList(faqRef, "smooth");
          break;
        default:
          break;
      }
    };

    const handleScroll = () => {
      if (forwardedRef && "current" in forwardedRef && forwardedRef.current) {
        const rect = forwardedRef.current.getBoundingClientRect();
        setScroll(rect.top <= window.innerHeight);
      }
    };

    // MARK: 강의 상세 재진입 시 탭 초기화
    useEffect(() => {
      window.scrollTo(0, 0);
      setTab(INIT_KLASS_DETAIL_TAB_KEY);
    }, [courseId]);

    useEffect(() => {
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }, []);

    return (
      <>
        <div className="z-20 mt-[24px] w-full h-[52px] max-w-[1200px] m-auto border-b-[1px] border-secondary-900 flex items-center justify-around text-lg text-[#333] sticky top-[53px] bg-secondary-100">
          {KLASS_DETAIL_TAB_KEYS.filter(
            (klassDetailTabKey) =>
              klassDetailTabKey === "intro" ||
              (klassDetailTabKey === "curriculum" && !isOfflineClass(lectureList.course)) ||
              (klassDetailTabKey === "recommend" && isShowRecommendTab) ||
              klassDetailTabKey === "faq",
          ).map((klassDetailTabKey, klassDetailTabKeyIndex) => {
            return (
              <div
                key={klassDetailTabKeyIndex}
                className={cls(
                  tab === klassDetailTabKey ? "text-white bg-secondary-900" : "",
                  isOfflineClass(lectureList.course) ? "w-[50%]" : "w-1/3",
                  "cursor-pointer border-solid flex items-center justify-center h-full relative group",
                )}
                onClick={() => onTabClick(klassDetailTabKey)}
              >
                {KLASS_DETAIL_TAB[klassDetailTabKey]}
              </div>
            );
          })}
        </div>
        <div className="max-w-[1200px] px-4 m-auto lg:px-0">
          <div className="w-full">
            <div className="mt-[48px]">
              <h3
                className="text-[#333] font-bold text-[22px] mb-[24px] leading-[24px]"
                ref={introRef}
              >
                강의소개
              </h3>

              <div
                className={cls(
                  moreIntro ? "" : "min-h-[100px] max-h-[750px] overflow-hidden",
                  "w-full",
                )}
              >
                {lectureList.course?.page?.editorType === "proseMirror" ? (
                  <ZoomImg>
                    <ProseMirrorEditorView
                      contentString={lectureList.course?.page?.contentString}
                    />
                  </ZoomImg>
                ) : (
                  <ZoomImg>
                    <EditorView content={lectureList.course?.page.content} />
                  </ZoomImg>
                )}
              </div>
              {!moreIntro && (
                <div className="mt-[24px]">
                  <button
                    className="w-full h-[61px] p-[20px] rounded-[10px] leading-[21px] border border-solid border-[#ccc] text-[#333] text-[18px] font-semibold flex items-center justify-center hover:bg-secondary-50"
                    onClick={() => setMoreIntro(true)}
                  >
                    강의소개 전체보기
                    <i className="ii ii-chevron-down ii-weight-600 text-[20px] text-[#333] ml-3"></i>
                  </button>
                </div>
              )}
            </div>
            <div ref={forwardedRef}>
              {!isOfflineClass(lectureList?.course) && (
                <Curriculum
                  progress={lectureList.progress}
                  course={lectureList.course}
                  defaultCourseId={defaultCourseId}
                />
              )}
            </div>
            <div ref={recommendRef}>
              {lecturerOtherCourses.length > 0 && (
                <div className="mt-[48px]">
                  <h3 className="text-[#333] font-bold text-[22px] mb-[24px] leading-[24px]">
                    강연자의 또 다른 강의
                  </h3>
                  <SwiperKlass
                    options={{
                      breakpoints: {
                        0: {
                          slidesPerView: isMobile ? 1.3 : 2.3,
                          spaceBetween: isMobile ? 14 : 8,
                        },
                      },
                    }}
                    navigationTop="top-[74px]"
                  >
                    {lecturerOtherCourses.map((item) => (
                      <SwiperSlide key={item?.courseId}>
                        <Link
                          to={`/${CLASS_ROUTE}/course/${item?.courseId}`}
                          className="block pb-[10px]"
                        >
                          <CourseCard
                            thumbnail={item?.thumbnailImage}
                            lecturers={item?.lecturers}
                            title={item?.title}
                            topic={item?.groupedBadges?.topics}
                            topicCategory={item?.groupedBadges?.topicCategory}
                            level={item?.groupedBadges?.level?.join(",")}
                            regularPrice={item?.regularPrice}
                            salePrice={item?.salePrice}
                            isSwiper
                            isFree={item?.isFree}
                          />
                        </Link>
                      </SwiperSlide>
                    ))}
                  </SwiperKlass>
                </div>
              )}
              {courseRelatedSeminars.length > 0 && (
                <div className="mt-[48px] pl-[15px] lg:pl-0 relative">
                  <h3 className="text-[#333] font-bold text-[22px] mb-[24px] leading-[24px]">
                    추천 세미나
                  </h3>
                  <Swiper
                    className="pb-[10px]"
                    direction="horizontal"
                    slidesPerView={2.3}
                    spaceBetween={isMobile ? 14 : 24}
                    modules={[Navigation]}
                    watchSlidesProgress={true}
                    observer={true}
                    observeParents={true}
                    navigation={{
                      nextEl: ".swiper-button-next1",
                      prevEl: ".swiper-button-prev1",
                    }}
                  >
                    {courseRelatedSeminars.map((item) => (
                      <SwiperSlide key={item.article.bid}>
                        <RecommendedSeminarItem
                          bid={item.article.bid}
                          thumbnail={item.article.thumbnail}
                          topic={item.article.topic}
                          title={item.article.title}
                          location={item.article.locationCities}
                          startDate={item.article.start_date}
                          endDate={item.article.end_date}
                          point={item.article.point}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  <div className="hidden lg:block">
                    <button className="swiper-button-next1 absolute w-9 h-9 rounded-full shadow-custom z-10 bg-white -right-5 top-[120px] disabled:hidden">
                      <i className="ii ii-chevron-right ii-weight-600 text-[20px] flex items-center justify-center" />
                    </button>
                    <button className="swiper-button-prev1 absolute w-9 h-9 rounded-full shadow-custom z-10 bg-white -left-5 top-[120px] disabled:hidden">
                      <i className="ii ii-chevron-left ii-weight-600 text-[20px] flex items-center justify-center" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-[48px]">
              <h3
                className="text-[#333] font-bold text-[22px] mb-[24px] leading-[24px]"
                ref={faqRef}
              >
                FAQ 및 공지
              </h3>
              {lectureList.course?.announcement.page?.editorType === "proseMirror" ? (
                <ProseMirrorEditorView
                  contentString={lectureList.course?.announcement.page?.contentString}
                />
              ) : (
                <EditorView content={lectureList.course?.announcement.page?.content} />
              )}
            </div>

            {isMobile && scroll && (
              <LectureFooter
                course={lectureList.course}
                progress={lectureList.progress}
                promotionTxt={lectureList.course.description}
                defaultCourseId={defaultCourseId}
              />
            )}
          </div>
        </div>
      </>
    );
  },
);

export default LectureContent;
